import React, { useState, useEffect } from "react";
import { LinearProgress, Container, withStyles, Fab, ImageList, ImageListItem } from "@material-ui/core";
import { HelpscoutFab } from "components";
import { Club } from "api/clubs";
import { useScrollBottom } from "utils/hooks";
import { FeedItem } from "api/feed";
import { Edit as EditIcon } from "@material-ui/icons";
import { UserProfile } from "api/user";
import CreateStoryDialog from "features/admin/BookingsView/CreateStoryDialog";
import i18n from "./i18n";
import StoryFeedCard from "./StoryFeedCard";
import BookingFeedCard from "./BookingFeedCard";
import "./homeView.scss";

// prop types
interface HomeViewProps {
    club: Club | null,
    feedItems: FeedItem[],
    isLoading: boolean,
    offset: number,
    userProfile: UserProfile | null,
    onFetchFeed: (clubId: string, offset: number) => void,
}

// home view showing a feed of invitations, upcoming bookings and stories
const HomeView = ({ club, feedItems, isLoading, offset, userProfile, onFetchFeed }: HomeViewProps) => {
    // ui state
    const [showCreateStoryDialog, setShowCreateStoryDialog] = useState(false);

    // fetch feed
    useEffect(() => { if (club) onFetchFeed(club!.id, 0); }, [club]); // eslint-disable-line

    useEffect(() => { if (!showCreateStoryDialog && club) onFetchFeed(club!.id, 0); }, [club, showCreateStoryDialog]);

    // scroll bottom handler
    const isBottom = useScrollBottom();
    useEffect(() => { if (isBottom && !isLoading) onFetchFeed(club!.id, offset); }, [isBottom]); // eslint-disable-line

    // can delete story (has to be author or admin of club)
    const userRole = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole;
    const canDeleteStory = (feedItem: FeedItem) => {
        if (feedItem.story?.author.id === userProfile?.id) return true;
        if (feedItem.story?.author.id === club?.id && ["admin", "owner"].includes(userRole!)) return true;
        return false;
    };

    // render
    const OverflowTile = withStyles({ item: { overflow: "visible", wordBreak: "break-word" } })(ImageListItem);
    return (
        <Container maxWidth="sm" className="containerMargin">
            <HelpscoutFab club={club} />
            {/* feed */}
            <ImageList cols={1} rowHeight="auto" gap={8}>
                {feedItems.map((feedItem) => (
                    <OverflowTile key={feedItem.booking?.id || feedItem.story?.id}>
                        {feedItem.type === "booking" && <BookingFeedCard feedItem={feedItem} />}
                        {feedItem.type === "story" && <StoryFeedCard allowMoreOptions={canDeleteStory(feedItem)} feedItem={feedItem} />}
                    </OverflowTile>
                ))}
            </ImageList>

            {/* loading */}
            {isLoading && <LinearProgress />}

            {/* new story fab */}
            {["admin", "owner"].includes(userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole!) && (
                <Fab
                    color="primary"
                    className="newStoryButton"
                    variant="extended"
                    onClick={() => setShowCreateStoryDialog(true)}
                >
                    <EditIcon className="iconMargin" />
                    <span className="iconMargin">{i18n.newStory}</span>
                </Fab>
            )}

            {/* create story dialog */}
            {showCreateStoryDialog && (
                <CreateStoryDialog
                    show={showCreateStoryDialog}
                    userProfile={userProfile}
                    onClose={() => { setShowCreateStoryDialog(false); }}
                />
            )}
        </Container>
    );
};

export default HomeView;
